import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage, getWineTypeLabel } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import FavoriteButton from "@/components/FavoriteButton";
import { getRatingClassesCompact } from "@/lib/ratingStyles";
import { resolveImageUrl } from "@/lib/apiBase";

const typeColors = {
  red: "bg-merlot/20 text-merlot-light border-merlot/30",
  white: "bg-gold/10 text-gold border-gold/30",
  sparkling: "bg-gold-light/10 text-gold-light border-gold-light/30",
  rose: "bg-pink-500/10 text-pink-400 border-pink-500/30",
};

export default function WineCard({ wine, disableFavorite = false }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const showPremium = !!user; // price + market_value + favorite are registered-only
  const typeClass = typeColors[wine.wine_type] || typeColors.red;
  const borderClass = `wine-type-${wine.wine_type || "red"}`;
  const ratingClass = getRatingClassesCompact(wine.rating);

  return (
    <div className="relative group">
      {showPremium && !disableFavorite && (
        <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
          <FavoriteButton wineId={wine.id} />
        </div>
      )}
      <Link to={`/wines/${wine.id}`} data-testid={`wine-card-${wine.id}`}>
        <div className={`glass-card ${borderClass} rounded-sm p-5 h-full flex flex-col justify-between`}>
          <div>
            {wine.image_url && (
              <div className="-mx-5 -mt-5 mb-4 h-48 bg-gradient-to-b from-white/[0.06] to-transparent flex items-center justify-center overflow-hidden rounded-t-sm">
                <img
                  src={resolveImageUrl(wine.image_url)}
                  alt={wine.name}
                  loading="lazy"
                  data-testid={`wine-card-image-${wine.id}`}
                  className="h-full w-auto object-contain py-3 transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <span className="font-accent text-xs tracking-[0.2em] text-white/40">{wine.year}</span>
              <Badge variant="outline" className={`text-[10px] font-body tracking-wider border ${typeClass}`}>
                {getWineTypeLabel(wine.wine_type, t)}
              </Badge>
            </div>
            <h3 className="font-display text-lg text-white/90 leading-tight mb-1">{wine.name}</h3>
            <p className="font-body text-xs text-white/40 tracking-wide mb-3">{wine.producer}</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              <Badge variant="outline" className="text-[10px] font-body text-white/50 border-white/10">{wine.region}</Badge>
              <Badge variant="outline" className="text-[10px] font-body text-white/50 border-white/10">{wine.grape_variety}</Badge>
            </div>
          </div>
          <div className="flex items-end justify-between mt-2 pt-3 border-t border-white/5">
            <div className={`px-2.5 py-1 rounded-sm font-display text-sm font-bold border ${ratingClass}`} data-testid="wine-score-badge">
              {wine.rating}
            </div>
            <div className="text-right">
              {showPremium ? (
                <>
                  <p className="font-body text-lg font-medium text-white/90">${wine.price?.toFixed(0)}</p>
                  {wine.market_value > wine.price && (
                    <p className="font-body text-[10px] text-gold/60">{t.mv} ${wine.market_value?.toFixed(0)}</p>
                  )}
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] text-white/40 font-body">
                  <Lock className="w-3 h-3" /> Members
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
